import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AssignmentOutlined as AssignmentOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  RestaurantMenuOutlined as RestaurantMenuOutlinedIcon,
  ViewInArOutlined as ViewInArOutlinedIcon,
  DirectionsCarOutlined as DirectionsCarOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  ArrowForwardOutlined as ArrowForwardOutlinedIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  WarehouseOutlined as WarehouseOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  YardOutlined as YardOutlinedIcon,
} from "../../../components/icons";
import { OperationsPageShell, ifasColors } from "./components/OperationsUI";
import { useSessionStore } from "../../../store/useSessionStore";
import { useUserStore } from "../../../store/useUserStore";
import { canAccessIfasModule } from "../../../utils/ifasAccess";

const services = [
  {
    title: "Funeral Planning",
    description: "Manage planner queue, statutory checks, payment readiness, operations, and feedback.",
    icon: <EventNoteOutlinedIcon />,
    action: "Open Planning",
    key: "funeralPlanning",
    accent: "#EC6E00",
  },
  {
    title: "Case Portal",
    description: "Create, assign, and monitor funeral administration cases.",
    icon: <AssignmentOutlinedIcon />,
    action: "Open Cases",
    key: "cases",
    accent: "#EC6E00",
  },
  {
    title: "Body Removal",
    description: "Track body removal orders, collection, and handover work.",
    icon: <LocalShippingOutlinedIcon />,
    action: "Open Removals",
    key: "bodyRemoval",
    accent: "#1B56EB",
  },
  {
    title: "Quotations",
    description: "Review quotation requests and service pricing details.",
    icon: <DescriptionOutlinedIcon />,
    action: "Open Quotes",
    key: "quotations",
    accent: "#6F42C1",
  },
  {
    title: "Mortuary",
    description:
      "Register intake, manage body control stages, and prepare dispatch-ready mortuary records.",
    icon: <Inventory2OutlinedIcon />,
    action: "Open Mortuary",
    key: "mortuary",
    accent: "#00897B",
  },
  {
    title: "Body Dispatch",
    description:
      "Coordinate internal transfers, external parlour releases, and delivery to family.",
    icon: <AssignmentTurnedInOutlinedIcon />,
    action: "Open Dispatch",
    key: "bodyDispatch",
    accent: "#8E24AA",
  },
  {
    title: "Warehouse",
    description:
      "Prepare funeral materials, manage dispatch and returns, and capture broken or lost items.",
    icon: <WarehouseOutlinedIcon />,
    action: "Open Warehouse",
    key: "warehouse",
    accent: "#795548",
  },
  {
    title: "Memorials",
    description: "Manage memorial enquiries, appointments, plots, payments, certificates, and ownership records.",
    icon: <YardOutlinedIcon />,
    action: "Open Memorials",
    key: "memorials",
    accent: "#00897B",
  },
  {
    title: "Tombstones",
    description: "Manage tombstone requests, installation, and cemetery data.",
    icon: <ViewInArOutlinedIcon />,
    action: "Open Tombstones",
    key: "tombstone",
    accent: "#2E7D32",
  },
  {
    title: "Catering",
    description: "Plan catering setup, menu, staffing, and delivery logistics.",
    icon: <RestaurantMenuOutlinedIcon />,
    action: "Open Catering",
    key: "catering",
    accent: "#F59B3C",
  },
  {
    title: "Fleet",
    description: "Coordinate drivers, vehicles, and operational assignments.",
    icon: <DirectionsCarOutlinedIcon />,
    action: "Open Fleet",
    key: "fleet",
    accent: "#00ACC1",
  },
  {
    title: "IFAS Reports Module",
    description: "Operational, tactical, and executive reports for funerals, mortuary, fleet, catering & inventory.",
    icon: <DescriptionOutlinedIcon />,
    action: "Open IFAS Reports",
    key: "ifasReports",
    accent: "#1976d2",
  },
];

export default function IFASHome({
  isDriver = false,
  role,
  onCasePortalClicked,
  onFuneralPlanningClicked,
  onBodyRemovalClicked,
  onMortuaryClicked,
  onWarehouseClicked,
  onBodyDispatchClicked,
  onQuotationClicked,
  onMemorialsPortalClicked,
  onTombstonePortalClicked,
  onCateringPortalClicked,
  onFleetClicked,
  onDriversClicked,
  onIfasReportsClicked,
}: any) {
  const storedRole = useUserStore((state) => state.role);
  const { userRole } = useSessionStore();
  const effectiveRole = role || userRole || storedRole;
  const visibleServices = services.filter((service) => {
    if (isDriver) {
      return ["bodyRemoval", "drivers"].includes(service.key);
    }

    const moduleKeyByService: Record<string, any> = {
      cases: "casePortal",
      funeralPlanning: "funeralPlanning",
      bodyRemoval: "bodyRemoval",
      quotations: "quotations",
      mortuary: "mortuary",
      bodyDispatch: "bodyDispatch",
      warehouse: "warehouse",
      tombstone: "tombstone",
      memorials: "memorials",
      catering: "catering",
      fleet: "fleet",
      ifasReports: "ifasReports",
    };

    const moduleKey = moduleKeyByService[service.key];
    return moduleKey ? canAccessIfasModule(effectiveRole, moduleKey) : false;
  });

  const handleOpen = (key: string) => {
    if (key === "cases") onCasePortalClicked?.();
    if (key === "funeralPlanning") onFuneralPlanningClicked?.();
    if (key === "bodyRemoval") onBodyRemovalClicked?.();
    if (key === "mortuary") onMortuaryClicked?.();
    if (key === "warehouse") onWarehouseClicked?.();
    if (key === "bodyDispatch") onBodyDispatchClicked?.();
    if (key === "quotations") onQuotationClicked?.();
    if (key === "memorials") onMemorialsPortalClicked?.();
    if (key === "tombstone") onTombstonePortalClicked?.();
    if (key === "catering") onCateringPortalClicked?.();
    if (key === "fleet") onFleetClicked?.();
    if (key === "ifasReports") onIfasReportsClicked?.();
  };

  return (
    <OperationsPageShell
      eyebrow={isDriver ? "IFAS Driver" : "IFAS System"}
      title={isDriver ? "Driver Workspace" : "Funeral Administration System"}
      description={
        isDriver
          ? "Your assigned removal work and driver record stay close together, so the next operational step is easy to find."
          : "A focused workspace for funeral administration, service planning, catering, tombstones, and fleet operations."
      }
    >
      <Grid container spacing={2}>
        {visibleServices.map((service) => (
          <Grid
            key={service.key}
            size={{
              xs: 12,
              sm: 6,
              lg: 4,
            }}
            
          >
            <Paper
              elevation={0}
              onClick={() => handleOpen(service.key)}
              sx={{
                p: 2.5,
                minHeight: 220,
                borderRadius: 2,
                border: `1px solid ${ifasColors.border}`,
                background: `linear-gradient(135deg, #FFFFFF 0%, ${alpha(
                  ifasColors.orange,
                  0.055,
                )} 100%)`,
                boxShadow: "0 8px 22px rgba(20, 24, 31, 0.045)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: alpha(ifasColors.orange, 0.35),
                  boxShadow: "0 14px 30px rgba(20, 24, 31, 0.08)",
                },
              }}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" gap={1.5}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      bgcolor: alpha(ifasColors.orange, 0.12),
                      color: ifasColors.orange,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": { fontSize: 23 },
                    }}
                  >
                    {service.icon}
                  </Box>
                </Box>
                <Typography
                  fontSize={18}
                  fontWeight={900}
                  color={ifasColors.text}
                  textAlign="left"
                  mt={1.5}
                >
                  {service.title}
                </Typography>
                <Typography
                  fontSize={13.5}
                  color={ifasColors.muted}
                  lineHeight={1.55}
                  textAlign="left"
                  mt={0.5}
                >
                  {service.description}
                </Typography>
              </Box>

              <Button
                variant="text"
                endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpen(service.key);
                }}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  borderRadius: 2,
                  color: ifasColors.orange,
                  fontWeight: 900,
                  mt: 1.5,
                  px: 0,
                  "&:hover": {
                    bgcolor: "transparent",
                    color: ifasColors.deepOrange,
                  },
                }}
              >
                {service.action}
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </OperationsPageShell>
  );
}
